import { delegateEventListener } from './utils/utils';

const AccessibilityBehaviors = {
  selectors: {
    menu: '[role="menu"]',
    buttonReveal: 'button[aria-controls][aria-expanded]'
  },
  attachEventListeners() {
    delegateEventListener('click', this.selectors.buttonReveal, this.onClickButtonReveal);
    delegateEventListener('keydown', this.selectors.menu, this.onKeydownNavigateMenu);
    delegateEventListener('keydown', 'a[role="button"]', this.onKeydownSpaceAsClick);

    // Focus element that matches hash
    this.focusHashLink();
    window.addEventListener('hashchange', this.focusHashLink);
  },

  focusHashLink() {
    try {
      if (location.hash && location.hash.length > 1) {
        const focusElement = document.querySelector(location.hash) as HTMLElement;
        if (focusElement) {
          focusElement.setAttribute('aria-expanded', 'true');
          focusElement.focus();
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e);
    }
  },

  onClickButtonReveal(button: HTMLElement) {
    const newExpandedValue = button.getAttribute('aria-expanded') === 'true' ? 'false' : 'true';
    button.setAttribute('aria-expanded', newExpandedValue);
    if (button.getAttribute('id')) {
      history.replaceState(null, '', `#${button.getAttribute('id')}`);
    }
  },

  onKeydownSpaceAsClick(target: HTMLElement, e: KeyboardEvent) {
    if (e.code === 'Space') {
      /**
       * Space key should stop scroll and trigger anchor links.
       */
      e.preventDefault();
      target.click();
    }
  },

  onKeydownNavigateMenu(menu: HTMLElement, e: KeyboardEvent) {
    const querySelectors = 'a, button';
    const handleArrowRight = (menu, e) => {
      e.preventDefault();
      const menuParent = menu.parentElement;
      const currentMenuIndex = Array.from(menuParent.children).indexOf(menu);
      const fallbackIndex = 0;
      const newIndex = currentMenuIndex + 1;
      const newTargetMenu = menuParent.children[newIndex] || menuParent.children[fallbackIndex];
      newTargetMenu.querySelector(querySelectors)?.focus();
    };

    const handleArrowLeft = (menu, e) => {
      e.preventDefault();
      const menuParent = menu.parentElement;
      const currentMenuIndex = Array.from(menuParent.children).indexOf(menu);
      const fallbackIndex = menuParent.children.length - 1;
      const newIndex = currentMenuIndex - 1;
      const newTargetMenu = menuParent.children[newIndex] || menuParent.children[fallbackIndex];
      newTargetMenu.querySelector(querySelectors)?.focus();
    };

    const handleArrowUp = (menu, e) => {
      e.preventDefault();
      const allSiblingsOfFocusedElement = menu.querySelectorAll(document.activeElement?.tagName);
      const currentActiveElementIndex = Array.from(allSiblingsOfFocusedElement).indexOf(
        document.activeElement
      );
      const fallbackActiveIndex = 0;
      const newActiveIndex = currentActiveElementIndex - 1;
      const newFocusElement =
        allSiblingsOfFocusedElement[newActiveIndex] ||
        allSiblingsOfFocusedElement[fallbackActiveIndex];
      newFocusElement.focus();
    };

    const handleArrowDown = (menu, e) => {
      e.preventDefault();
      const allSiblingsOfFocusedElement = menu.querySelectorAll(document.activeElement?.tagName);
      const currentActiveElementIndex = Array.from(allSiblingsOfFocusedElement).indexOf(
        document.activeElement
      );
      const fallbackActiveIndex = allSiblingsOfFocusedElement.length - 1;
      const newActiveIndex = currentActiveElementIndex + 1;
      const newFocusElement =
        allSiblingsOfFocusedElement[newActiveIndex] ||
        allSiblingsOfFocusedElement[fallbackActiveIndex];
      newFocusElement.focus();
    };

    switch (e.code) {
      case 'ArrowRight':
        handleArrowRight(menu, e);
        break;
      case 'ArrowLeft':
        handleArrowLeft(menu, e);
        break;
      case 'ArrowUp':
        handleArrowUp(menu, e);
        break;
      case 'ArrowDown':
        handleArrowDown(menu, e);
        break;
      default:
        break;
    }
  }
};

AccessibilityBehaviors.attachEventListeners();
